// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Survivor Detail
// Full profile view with tabs: Overview, Medical, Assets, History
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, TextInput, Select, Textarea, Table, Badge, Button, Group, Stack,
  Paper, Modal, Loader, Center, Tabs, SimpleGrid, Divider, Avatar,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft, IconUser, IconStethoscope, IconCar, IconHistory,
  IconEdit, IconDeviceFloppy, IconLogout, IconTransfer, IconPlus,
  IconPhone, IconMail, IconHome, IconPaw, IconPackage,
  IconNotes, IconClipboardList, IconMedicineSyrup, IconPrinter, IconTags, IconCheck,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { SURVIVOR_STATUS_COLORS, SPECIAL_NEEDS_OPTIONS, DIETARY_OPTIONS, REFERRAL_TYPES } from '../../types/shelter';
import type { SurvivorStatus, Survivor } from '../../types/shelter';
import { printPropertyLabelSheet } from '../../lib/printLabels';
import { formatPhone } from '../../lib/phoneFormat';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

// ── Helpers ──────────────────────────────────────────────────

function safeParse<T>(json: string, fallback: T): T {
  try { return JSON.parse(json) as T; } catch { return fallback; }
}

function labelFor(value: string, opts: { value: string; label: string }[]): string {
  return opts.find(o => o.value === value)?.label ?? value;
}

function formatDate(iso: string): string {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// ── Component ────────────────────────────────────────────────

export default function SurvivorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const {
    survivors, shelters, families, pets, vehicles, property,
    medicalLog, triageAnswers, triageQuestions, transfers, referrals,
    loadingSurvivors,
    loadSurvivors, loadShelters, loadFamilies, loadPets, loadVehicles,
    loadProperty, loadMedicalLog, loadTriageAnswers, loadTriageQuestions,
    loadTransfers, loadReferrals,
    updateSurvivor, createMedicalEntry, createReferral, createTransfer,
  } = useShelterStore();

  // ── Load all related data ─────────────────────────────────
  useEffect(() => {
    loadSurvivors();
    loadShelters();
    loadFamilies();
    loadPets();
    loadVehicles();
    loadProperty();
    loadMedicalLog();
    loadTriageAnswers();
    loadTriageQuestions();
    loadTransfers();
    loadReferrals();
  }, [
    loadSurvivors, loadShelters, loadFamilies, loadPets, loadVehicles,
    loadProperty, loadMedicalLog, loadTriageAnswers, loadTriageQuestions,
    loadTransfers, loadReferrals,
  ]);

  // ── Find survivor ─────────────────────────────────────────
  const survivor = useMemo(
    () => survivors.find(s => String(s.id) === id) ?? null,
    [survivors, id],
  );

  // ── Shelter lookup ────────────────────────────────────────
  const shelterMap = useMemo(() => {
    const m: Record<string, string> = {};
    shelters.forEach(s => { m[String(s.id)] = s.name; });
    return m;
  }, [shelters]);

  // ── Related data for this survivor ────────────────────────
  const sid = id ?? '';

  const familyMembers = useMemo(() => {
    if (!survivor?.familyId) return [];
    return survivors.filter(s => s.familyId === survivor.familyId && String(s.id) !== sid);
  }, [survivors, survivor, sid]);

  const family = useMemo(
    () => (survivor?.familyId ? families.find(f => String(f.Rid) === survivor.familyId || f.memberIds.includes(sid)) : null),
    [families, survivor, sid],
  );

  const myPets = useMemo(() => pets.filter(p => p.ownerId === sid), [pets, sid]);
  const myVehicles = useMemo(() => vehicles.filter(v => v.ownerId === sid), [vehicles, sid]);
  const myProperty = useMemo(() => property.filter(p => p.ownerId === sid), [property, sid]);
  const myMedical = useMemo(
    () => [...medicalLog.filter(m => m.survivorId === sid)].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    ),
    [medicalLog, sid],
  );
  const myTriage = useMemo(() => triageAnswers.filter(a => a.survivorId === sid), [triageAnswers, sid]);
  const myTransfers = useMemo(
    () => [...transfers.filter(t => t.survivorId === sid)].sort(
      (a, b) => new Date(b.initiatedAt).getTime() - new Date(a.initiatedAt).getTime(),
    ),
    [transfers, sid],
  );
  const myReferrals = useMemo(
    () => [...referrals.filter(r => r.survivorId === sid)].sort(
      (a, b) => new Date(b.dateMade).getTime() - new Date(a.dateMade).getTime(),
    ),
    [referrals, sid],
  );

  // ── Triage question lookup ────────────────────────────────
  const qMap = useMemo(() => {
    const m: Record<string, string> = {};
    triageQuestions.forEach(q => { m[String(q.id)] = q.question; });
    return m;
  }, [triageQuestions]);

  // ── Edit mode ─────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Survivor>>({});
  const [saveLoading, setSaveLoading] = useState(false);

  const startEditing = useCallback(() => {
    if (!survivor) return;
    setEditData({
      firstName: survivor.firstName,
      lastName: survivor.lastName,
      dateOfBirth: survivor.dateOfBirth,
      gender: survivor.gender,
      phone: survivor.phone,
      email: survivor.email,
      homeAddress: survivor.homeAddress,
      city: survivor.city,
      state: survivor.state,
      zip: survivor.zip,
      county: survivor.county,
      cotAssignment: survivor.cotAssignment,
      emergencyContactName: survivor.emergencyContactName,
      emergencyContactPhone: survivor.emergencyContactPhone,
      notes: survivor.notes,
    });
    setEditing(true);
  }, [survivor]);

  const handleSaveEdit = useCallback(async () => {
    if (!survivor) return;
    setSaveLoading(true);
    try {
      await updateSurvivor(survivor.Rid, editData);
      setEditing(false);
      notifications.show({ title: 'Updated', message: 'Survivor record updated.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Save failed.', color: 'red' });
    } finally {
      setSaveLoading(false);
    }
  }, [survivor, editData, updateSurvivor]);

  // ── Discharge modal ───────────────────────────────────────
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeLoading, setDischargeLoading] = useState(false);

  const handleDischarge = useCallback(async () => {
    if (!survivor || !dischargeReason.trim()) return;
    setDischargeLoading(true);
    try {
      const actorName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Unknown';
      await updateSurvivor(survivor.Rid, {
        status: 'discharged',
        dischargeTimestamp: new Date().toISOString(),
        dischargeReason: dischargeReason.trim(),
        dischargedBy: actorName,
      });
      setDischargeOpen(false);
      setDischargeReason('');
      notifications.show({ title: 'Discharged', message: 'Survivor discharged.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Discharge failed.', color: 'red' });
    } finally {
      setDischargeLoading(false);
    }
  }, [survivor, dischargeReason, user, updateSurvivor]);

  // ── Transfer modal ────────────────────────────────────────
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState<string | null>(null);
  const [transferReason, setTransferReason] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  const shelterSelectData = useMemo(
    () => shelters.filter(s => String(s.id) !== survivor?.shelterId).map(s => ({ value: String(s.id), label: s.name })),
    [shelters, survivor],
  );

  const handleTransfer = useCallback(async () => {
    if (!survivor || !transferTo) return;
    setTransferLoading(true);
    try {
      const actorName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Unknown';
      await createTransfer({
        incidentId: survivor.incidentId,
        survivorId: sid,
        survivorName: `${survivor.firstName} ${survivor.lastName}`,
        fromShelterId: survivor.shelterId,
        fromShelterName: shelterMap[survivor.shelterId] || '',
        toShelterId: transferTo,
        toShelterName: shelterMap[transferTo] || '',
        reason: transferReason,
        status: 'pending',
        initiatedBy: actorName,
        initiatedAt: new Date().toISOString(),
      });
      await updateSurvivor(survivor.Rid, { status: 'transferred', shelterId: transferTo });
      setTransferOpen(false);
      setTransferTo(null);
      setTransferReason('');
      notifications.show({ title: 'Transferred', message: 'Transfer initiated.', color: 'blue' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Transfer failed.', color: 'red' });
    } finally {
      setTransferLoading(false);
    }
  }, [survivor, transferTo, transferReason, user, sid, shelterMap, createTransfer, updateSurvivor]);

  // ── Add Medical Entry modal ───────────────────────────────
  const [medicalOpen, setMedicalOpen] = useState(false);
  const [medEntry, setMedEntry] = useState({ entryType: 'note', description: '', medicationName: '', dosage: '', notes: '' });
  const [medLoading, setMedLoading] = useState(false);

  const handleAddMedical = useCallback(async () => {
    if (!survivor) return;
    setMedLoading(true);
    try {
      const actorName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Unknown';
      await createMedicalEntry({
        incidentId: survivor.incidentId,
        survivorId: sid,
        shelterId: survivor.shelterId,
        entryType: medEntry.entryType,
        description: medEntry.description,
        medicationName: medEntry.medicationName,
        dosage: medEntry.dosage,
        administeredBy: actorName,
        timestamp: new Date().toISOString(),
        notes: medEntry.notes,
      });
      setMedicalOpen(false);
      setMedEntry({ entryType: 'note', description: '', medicationName: '', dosage: '', notes: '' });
      notifications.show({ title: 'Added', message: 'Medical entry recorded.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to add entry.', color: 'red' });
    } finally {
      setMedLoading(false);
    }
  }, [survivor, sid, medEntry, user, createMedicalEntry]);

  // ── Add Referral modal ────────────────────────────────────
  const [referralOpen, setReferralOpen] = useState(false);
  const [refEntry, setRefEntry] = useState({ referralType: 'fema', agency: '', notes: '' });
  const [refLoading, setRefLoading] = useState(false);

  const handleAddReferral = useCallback(async () => {
    if (!survivor) return;
    setRefLoading(true);
    try {
      const actorName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Unknown';
      await createReferral({
        incidentId: survivor.incidentId,
        survivorId: sid,
        survivorName: `${survivor.firstName} ${survivor.lastName}`,
        referralType: refEntry.referralType,
        agency: refEntry.agency,
        status: 'pending',
        dateMade: new Date().toISOString(),
        madeBy: actorName,
        notes: refEntry.notes,
      });
      setReferralOpen(false);
      setRefEntry({ referralType: 'fema', agency: '', notes: '' });
      notifications.show({ title: 'Added', message: 'Referral created.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to create referral.', color: 'red' });
    } finally {
      setRefLoading(false);
    }
  }, [survivor, sid, refEntry, user, createReferral]);

  // ── Parsed arrays ─────────────────────────────────────────
  const specialNeeds = useMemo(() => safeParse<string[]>(survivor?.specialNeeds ?? '[]', []), [survivor]);
  const dietaryNeeds = useMemo(() => safeParse<string[]>(survivor?.dietaryNeeds ?? '[]', []), [survivor]);
  const medications = useMemo(
    () => safeParse<Array<{ name: string; dosage: string; frequency: string; lastAdministered?: string }>>(survivor?.medications ?? '[]', []),
    [survivor],
  );

  // ── QR Code image generation ─────────────────────────────
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (survivor?.qrCode) {
      QRCode.toDataURL(survivor.qrCode, { width: 200, margin: 1 }).then(url => setQrDataUrl(url));
    }
  }, [survivor?.qrCode]);

  // ── Print ID Badge ─────────────────────────────────────────
  const handlePrintBadge = useCallback(async () => {
    if (!survivor) return;
    let qrImg = qrDataUrl;
    if (!qrImg && survivor.qrCode) {
      qrImg = await QRCode.toDataURL(survivor.qrCode, { width: 300, margin: 1 });
    }
    const shelterName = shelterMap[survivor.shelterId] || 'Unassigned';
    const intakeDate = survivor.intakeTimestamp ? new Date(survivor.intakeTimestamp).toLocaleDateString() : '';
    const w = window.open('', '_blank', 'width=400,height=600');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>ID Badge</title><style>
      @page { size: 3.5in 2.25in; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; width: 3.5in; padding: 10px; }
      .header { text-align: center; font-size: 8px; font-weight: 700; color: var(--color-primary-800); text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid var(--color-primary-800); padding-bottom: 4px; margin-bottom: 6px; }
      .name { text-align: center; font-size: 16px; font-weight: 700; margin-bottom: 4px; }
      .qr { text-align: center; margin: 4px 0; }
      .qr img { width: 150px; height: 150px; }
      .code { text-align: center; font-family: monospace; font-size: 9px; color: #555; margin-bottom: 6px; }
      .info { font-size: 9px; line-height: 1.5; }
      .info .label { font-weight: 700; color: #333; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    </style></head><body>
      <div class="header">OpenGov EMC &mdash; Shelter ID Badge</div>
      <div class="name">${survivor.firstName} ${survivor.lastName}</div>
      <div class="qr">${qrImg ? `<img src="${qrImg}" />` : ''}</div>
      <div class="code">${survivor.qrCode || ''}</div>
      <div class="info">
        <span class="label">Shelter:</span> ${shelterName}<br/>
        <span class="label">Cot:</span> ${survivor.cotAssignment || 'N/A'}<br/>
        <span class="label">Intake:</span> ${intakeDate}
      </div>
    </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  }, [survivor, qrDataUrl, shelterMap]);

  // ── Print Property Labels ─────────────────────────────────
  const handlePrintPropertyLabels = useCallback(async () => {
    if (!survivor) return;
    const ownerName = `${survivor.firstName} ${survivor.lastName}`;
    const items = myProperty
      .filter(p => !p.checkedOutAt)
      .map(p => ({ description: p.description, qrCode: p.qrCode, storageLocation: p.storageLocation }));
    // If they have items, print those labels. If not, print 6 blank labels with their QR.
    await printPropertyLabelSheet(ownerName, survivor.qrCode, items, items.length === 0 ? 6 : 0);
  }, [survivor, myProperty]);

  // ── Loading / not found ───────────────────────────────────
  if (loadingSurvivors && !survivor) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }
  if (!survivor) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Text fw={600}>Survivor not found</Text>
          <Button variant="subtle" onClick={() => navigate('/shelter/survivors')}>Back to Survivors</Button>
        </Stack>
      </Center>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <Stack gap="md">
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'Shelter', to: '/shelter' },
        { label: 'Survivors', to: '/shelter/survivors' },
        { label: survivor ? `${survivor.firstName} ${survivor.lastName}`.trim() || 'Survivor' : 'Survivor' },
      ]} />
      {/* ── Header ─────────────────────────────────────────── */}
      <Group justify="space-between">
        <Group gap="sm">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/shelter/survivors')}
          >
            Back
          </Button>
          <Avatar size={48} radius="xl" color="blue">
            {survivor.firstName?.[0]}{survivor.lastName?.[0]}
          </Avatar>
          {qrDataUrl && (
            <img src={qrDataUrl} alt="QR Code" style={{ width: 60, height: 60, borderRadius: 4 }} />
          )}
          <div>
            <Group gap="xs">
              <Title order={3} c="black">{survivor.firstName} {survivor.lastName}</Title>
              <Badge color={SURVIVOR_STATUS_COLORS[survivor.status as SurvivorStatus] ?? 'gray'}>
                {survivor.status ? survivor.status.replace(/_/g, ' ') : 'checked in'}
              </Badge>
            </Group>
            <Group gap="xs">
              {survivor.qrCode && (
                <Text size="xs" ff="monospace" c="dimmed">{survivor.qrCode}</Text>
              )}
              <Text size="xs" c="dimmed">ID: {survivor.id}</Text>
            </Group>
          </div>
        </Group>

        {/* Action buttons */}
        <Group gap="xs">
          <Button variant="light" color="gray" size="sm" leftSection={<IconPrinter size={14} />} onClick={handlePrintBadge}>
            Print ID Badge
          </Button>
          <Button variant="light" color="teal" size="sm" leftSection={<IconTags size={14} />} onClick={handlePrintPropertyLabels}>
            Print Property Labels
          </Button>
          {!editing && (
            <Button variant="light" size="sm" leftSection={<IconEdit size={14} />} onClick={startEditing}>
              Edit
            </Button>
          )}
          {survivor.status === 'pre-registered' && (
            <Button variant="filled" color="green" size="sm" leftSection={<IconCheck size={14} />}
              onClick={async () => {
                await updateSurvivor(survivor.Rid, { status: 'checked_in', intakeTimestamp: new Date().toISOString() });
                notifications.show({ message: 'Check-in completed!', color: 'green' });
              }}>
              Complete Check-In
            </Button>
          )}
          {survivor.status === 'checked_in' && (
            <>
              <Button variant="light" color="blue" size="sm" leftSection={<IconTransfer size={14} />} onClick={() => setTransferOpen(true)}>
                Transfer
              </Button>
              <Button variant="light" color="red" size="sm" leftSection={<IconLogout size={14} />} onClick={() => setDischargeOpen(true)}>
                Discharge
              </Button>
            </>
          )}
          <Button variant="light" color="teal" size="sm" leftSection={<IconPlus size={14} />} onClick={() => setMedicalOpen(true)}>
            Medical Entry
          </Button>
          <Button variant="light" color="violet" size="sm" leftSection={<IconPlus size={14} />} onClick={() => setReferralOpen(true)}>
            Referral
          </Button>
        </Group>
      </Group>

      {/* ── Tabs ───────────────────────────────────────────── */}
      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconUser size={14} />}>Overview</Tabs.Tab>
          <Tabs.Tab value="medical" leftSection={<IconStethoscope size={14} />}>Medical</Tabs.Tab>
          <Tabs.Tab value="assets" leftSection={<IconCar size={14} />}>Assets</Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>History</Tabs.Tab>
        </Tabs.List>

        {/* ════════════════════════════════════════════════════
            TAB: Overview
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="md">
            {/* Personal Info */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconUser size={16} />
                <Text fw={700}>Personal Information</Text>
              </Group>
              {editing ? (
                <Stack gap="sm">
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <TextInput label="First Name" value={editData.firstName || ''} onChange={e => setEditData({ ...editData, firstName: e.currentTarget.value })} />
                    <TextInput label="Last Name" value={editData.lastName || ''} onChange={e => setEditData({ ...editData, lastName: e.currentTarget.value })} />
                    <TextInput label="Date of Birth" type="date" value={editData.dateOfBirth || ''} onChange={e => setEditData({ ...editData, dateOfBirth: e.currentTarget.value })} />
                    <Select label="Gender" value={editData.gender || ''} onChange={v => setEditData({ ...editData, gender: v || '' })} data={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }, { value: 'unknown', label: 'Prefer not to say' }]} clearable />
                    <TextInput label="Phone" value={editData.phone || ''} onChange={e => setEditData({ ...editData, phone: formatPhone(e.currentTarget.value) })} />
                    <TextInput label="Email" value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.currentTarget.value })} />
                    <TextInput label="Home Address" value={editData.homeAddress || ''} onChange={e => setEditData({ ...editData, homeAddress: e.currentTarget.value })} />
                    <TextInput label="City" value={editData.city || ''} onChange={e => setEditData({ ...editData, city: e.currentTarget.value })} />
                    <TextInput label="State" value={editData.state || ''} onChange={e => setEditData({ ...editData, state: e.currentTarget.value })} />
                    <TextInput label="ZIP" value={editData.zip || ''} onChange={e => setEditData({ ...editData, zip: e.currentTarget.value })} />
                    <TextInput label="County" value={editData.county || ''} onChange={e => setEditData({ ...editData, county: e.currentTarget.value })} />
                    <TextInput label="Cot Assignment" value={editData.cotAssignment || ''} onChange={e => setEditData({ ...editData, cotAssignment: e.currentTarget.value })} />
                  </SimpleGrid>
                  <Textarea label="Notes" value={editData.notes || ''} onChange={e => setEditData({ ...editData, notes: e.currentTarget.value })} minRows={2} />
                  <Group justify="flex-end">
                    <Button variant="subtle" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                    <Button size="sm" leftSection={<IconDeviceFloppy size={14} />} onClick={handleSaveEdit} loading={saveLoading}>Save</Button>
                  </Group>
                </Stack>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                  <InfoField label="Date of Birth" value={survivor.dateOfBirth || '\u2014'} />
                  <InfoField label="Gender" value={survivor.gender || '\u2014'} />
                  <InfoField label="Phone" value={survivor.phone ? formatPhone(survivor.phone) : '\u2014'} icon={<IconPhone size={12} />} />
                  <InfoField label="Email" value={survivor.email || '\u2014'} icon={<IconMail size={12} />} />
                  <InfoField label="Home Address" value={[survivor.homeAddress, survivor.city, survivor.state, survivor.zip].filter(Boolean).join(', ') || '\u2014'} icon={<IconHome size={12} />} />
                  <InfoField label="County" value={survivor.county || '\u2014'} />
                  <InfoField label="Shelter" value={shelterMap[survivor.shelterId] || '\u2014'} />
                  <InfoField label="Cot Assignment" value={survivor.cotAssignment || '\u2014'} />
                  <InfoField label="Intake Date" value={formatDate(survivor.intakeTimestamp)} />
                  <InfoField label="Intake By" value={survivor.intakeBy || '\u2014'} />
                  {survivor.isMinor && <Badge color="orange" variant="light">Minor</Badge>}
                  {survivor.isUnaccompaniedMinor && <Badge color="red" variant="light">Unaccompanied Minor</Badge>}
                </SimpleGrid>
              )}
              {!editing && survivor.notes && (
                <>
                  <Divider my="sm" />
                  <Group gap={4} mb={4}><IconNotes size={12} /><Text size="xs" fw={600} c="dimmed">Notes</Text></Group>
                  <Text size="sm">{survivor.notes}</Text>
                </>
              )}
            </Paper>

            {/* Emergency Contact */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconPhone size={16} />
                <Text fw={700}>Emergency Contact</Text>
              </Group>
              {editing ? (
                <SimpleGrid cols={2} spacing="sm">
                  <TextInput label="Contact Name" value={editData.emergencyContactName || ''} onChange={e => setEditData({ ...editData, emergencyContactName: e.currentTarget.value })} />
                  <TextInput label="Contact Phone" value={editData.emergencyContactPhone || ''} onChange={e => setEditData({ ...editData, emergencyContactPhone: formatPhone(e.currentTarget.value) })} />
                </SimpleGrid>
              ) : (
                <SimpleGrid cols={2} spacing="sm">
                  <InfoField label="Name" value={survivor.emergencyContactName || '\u2014'} />
                  <InfoField label="Phone" value={survivor.emergencyContactPhone ? formatPhone(survivor.emergencyContactPhone) : '\u2014'} />
                </SimpleGrid>
              )}
            </Paper>

            {/* Special Needs & Dietary */}
            <Paper p="md" radius="md" withBorder>
              <Text fw={700} mb="sm">Special Needs & Dietary</Text>
              <Group gap="xs" mb="xs">
                <Text size="xs" fw={600} c="dimmed">Special Needs:</Text>
                {specialNeeds.length > 0
                  ? specialNeeds.map(n => <Badge key={n} size="sm" variant="light" color="orange">{labelFor(n, SPECIAL_NEEDS_OPTIONS)}</Badge>)
                  : <Text size="sm" c="dimmed">None</Text>
                }
              </Group>
              <Group gap="xs">
                <Text size="xs" fw={600} c="dimmed">Dietary Needs:</Text>
                {dietaryNeeds.length > 0
                  ? dietaryNeeds.map(n => <Badge key={n} size="sm" variant="light" color="teal">{labelFor(n, DIETARY_OPTIONS)}</Badge>)
                  : <Text size="sm" c="dimmed">None</Text>
                }
              </Group>
            </Paper>

            {/* Medications */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconMedicineSyrup size={16} />
                <Text fw={700}>Medications</Text>
              </Group>
              {medications.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Medication</Table.Th>
                      <Table.Th>Dosage</Table.Th>
                      <Table.Th>Frequency</Table.Th>
                      <Table.Th>Last Administered</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {medications.map((m, i) => (
                      <Table.Tr key={i}>
                        <Table.Td><Text size="sm">{m.name}</Text></Table.Td>
                        <Table.Td><Text size="sm">{m.dosage}</Text></Table.Td>
                        <Table.Td><Text size="sm">{m.frequency}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{m.lastAdministered ? formatDate(m.lastAdministered) : '\u2014'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No medications recorded</Text>
              )}
            </Paper>

            {/* Family */}
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={700}>Family</Text>
                {family && <Badge size="sm" variant="light">{family.familyName}</Badge>}
              </Group>
              {familyMembers.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Shelter</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {familyMembers.map(fm => (
                      <Table.Tr key={fm.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/shelter/survivor/${fm.id}`)}>
                        <Table.Td><Text size="sm" fw={500}>{fm.firstName} {fm.lastName}</Text></Table.Td>
                        <Table.Td><Badge size="sm" color={SURVIVOR_STATUS_COLORS[fm.status as SurvivorStatus] ?? 'gray'}>{fm.status.replace(/_/g, ' ')}</Badge></Table.Td>
                        <Table.Td><Text size="sm">{shelterMap[fm.shelterId] || '\u2014'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">
                  {survivor.familyId ? 'No other family members found' : 'Not assigned to a family'}
                </Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: Medical
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="medical" pt="md">
          <Stack gap="md">
            {/* Medical Log */}
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <IconStethoscope size={16} />
                  <Text fw={700}>Medical Log</Text>
                  <Badge size="sm" variant="light">{myMedical.length}</Badge>
                </Group>
                <Button size="compact-xs" variant="light" leftSection={<IconPlus size={12} />} onClick={() => setMedicalOpen(true)}>
                  Add Entry
                </Button>
              </Group>
              {myMedical.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Medication</Table.Th>
                      <Table.Th>By</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myMedical.map(m => (
                      <Table.Tr key={m.id}>
                        <Table.Td><Badge size="xs" variant="light">{m.entryType}</Badge></Table.Td>
                        <Table.Td><Text size="sm" lineClamp={2}>{m.description || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{m.medicationName ? `${m.medicationName} ${m.dosage}` : '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{m.administeredBy || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(m.timestamp)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No medical log entries</Text>
              )}
            </Paper>

            {/* Triage Answers */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconClipboardList size={16} />
                <Text fw={700}>Triage Answers</Text>
                <Badge size="sm" variant="light">{myTriage.length}</Badge>
              </Group>
              {myTriage.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Question</Table.Th>
                      <Table.Th>Answer</Table.Th>
                      <Table.Th>Flagged</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myTriage.map(a => (
                      <Table.Tr key={a.id}>
                        <Table.Td><Text size="sm">{qMap[a.questionId] || `Q#${a.questionId}`}</Text></Table.Td>
                        <Table.Td><Text size="sm" fw={500}>{a.answer}</Text></Table.Td>
                        <Table.Td>{a.isFlagged ? <Badge size="xs" color="red">Flagged</Badge> : <Text size="xs" c="dimmed">No</Text>}</Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(a.answeredAt)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No triage answers recorded</Text>
              )}
            </Paper>

            {/* Referrals */}
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Text fw={700}>Referrals</Text>
                  <Badge size="sm" variant="light">{myReferrals.length}</Badge>
                </Group>
                <Button size="compact-xs" variant="light" color="violet" leftSection={<IconPlus size={12} />} onClick={() => setReferralOpen(true)}>
                  Add Referral
                </Button>
              </Group>
              {myReferrals.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Agency</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Case #</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myReferrals.map(r => (
                      <Table.Tr key={r.id}>
                        <Table.Td><Badge size="xs" variant="light">{labelFor(r.referralType, REFERRAL_TYPES)}</Badge></Table.Td>
                        <Table.Td><Text size="sm">{r.agency || '\u2014'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={r.status === 'completed' ? 'green' : r.status === 'declined' ? 'red' : 'blue'}>{r.status}</Badge></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{r.caseNumber || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(r.dateMade)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No referrals</Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: Assets (Pets, Vehicles, Property)
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="assets" pt="md">
          <Stack gap="md">
            {/* Pets */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconPaw size={16} />
                <Text fw={700}>Pets</Text>
                <Badge size="sm" variant="light">{myPets.length}</Badge>
              </Group>
              {myPets.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Species</Table.Th>
                      <Table.Th>Breed</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Location</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myPets.map(p => (
                      <Table.Tr key={p.id}>
                        <Table.Td><Text size="sm" fw={500}>{p.name || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm" tt="capitalize">{p.species}</Text></Table.Td>
                        <Table.Td><Text size="sm">{p.breed || '\u2014'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" variant="light">{p.status}</Badge></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{p.locationInShelter || '\u2014'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No pets registered</Text>
              )}
            </Paper>

            {/* Vehicles */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconCar size={16} />
                <Text fw={700}>Vehicles</Text>
                <Badge size="sm" variant="light">{myVehicles.length}</Badge>
              </Group>
              {myVehicles.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Vehicle</Table.Th>
                      <Table.Th>Color</Table.Th>
                      <Table.Th>License</Table.Th>
                      <Table.Th>Parking</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myVehicles.map(v => (
                      <Table.Tr key={v.id}>
                        <Table.Td><Text size="sm" fw={500}>{[v.year, v.make, v.model].filter(Boolean).join(' ') || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{v.color || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm" ff="monospace">{v.licensePlate || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{v.parkingLocation || '\u2014'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" variant="light">{v.status}</Badge></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No vehicles registered</Text>
              )}
            </Paper>

            {/* Property */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconPackage size={16} />
                <Text fw={700}>Property</Text>
                <Badge size="sm" variant="light">{myProperty.length}</Badge>
              </Group>
              {myProperty.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Storage</Table.Th>
                      <Table.Th>Checked In</Table.Th>
                      <Table.Th>Checked Out</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myProperty.map(p => (
                      <Table.Tr key={p.id}>
                        <Table.Td><Text size="sm" fw={500}>{p.description}</Text></Table.Td>
                        <Table.Td><Badge size="xs" variant="light" tt="capitalize">{p.category}</Badge></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{p.storageLocation || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(p.checkedInAt)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(p.checkedOutAt)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No property items</Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: History (Transfers, Discharge info)
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="history" pt="md">
          <Stack gap="md">
            {/* Discharge info */}
            {survivor.status === 'discharged' && (
              <Paper p="md" radius="md" withBorder>
                <Text fw={700} mb="sm">Discharge Information</Text>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                  <InfoField label="Discharged At" value={formatDate(survivor.dischargeTimestamp)} />
                  <InfoField label="Discharged By" value={survivor.dischargedBy || '\u2014'} />
                  <InfoField label="Reason" value={survivor.dischargeReason || '\u2014'} />
                </SimpleGrid>
              </Paper>
            )}

            {/* Transfer History */}
            <Paper p="md" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconTransfer size={16} />
                <Text fw={700}>Transfer History</Text>
                <Badge size="sm" variant="light">{myTransfers.length}</Badge>
              </Group>
              {myTransfers.length > 0 ? (
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>From</Table.Th>
                      <Table.Th>To</Table.Th>
                      <Table.Th>Reason</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Initiated</Table.Th>
                      <Table.Th>Completed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {myTransfers.map(t => (
                      <Table.Tr key={t.id}>
                        <Table.Td><Text size="sm">{t.fromShelterName || shelterMap[t.fromShelterId] || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{t.toShelterName || shelterMap[t.toShelterId] || '\u2014'}</Text></Table.Td>
                        <Table.Td><Text size="sm" lineClamp={2}>{t.reason || '\u2014'}</Text></Table.Td>
                        <Table.Td>
                          <Badge size="xs" color={
                            t.status === 'completed' ? 'green' :
                            t.status === 'cancelled' ? 'red' :
                            t.status === 'in_transit' ? 'orange' : 'blue'
                          }>{t.status}</Badge>
                        </Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(t.initiatedAt)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDate(t.completedAt)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed">No transfers</Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* ═══════════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════════ */}

      {/* Discharge Modal */}
      <Modal opened={dischargeOpen} onClose={() => { setDischargeOpen(false); setDischargeReason(''); }} title="Discharge Survivor" size="md">
        <Stack gap="md">
          <Text size="sm">Discharge <strong>{survivor.firstName} {survivor.lastName}</strong> from shelter.</Text>
          <Textarea label="Reason" placeholder="Reason for discharge..." value={dischargeReason} onChange={e => setDischargeReason(e.currentTarget.value)} minRows={3} required />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { setDischargeOpen(false); setDischargeReason(''); }}>Cancel</Button>
            <Button color="red" onClick={handleDischarge} loading={dischargeLoading} disabled={!dischargeReason.trim()}>Confirm Discharge</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Transfer Modal */}
      <Modal opened={transferOpen} onClose={() => { setTransferOpen(false); setTransferTo(null); setTransferReason(''); }} title="Transfer Survivor" size="md">
        <Stack gap="md">
          <Text size="sm">Transfer <strong>{survivor.firstName} {survivor.lastName}</strong> to another shelter.</Text>
          <Select label="Destination Shelter" placeholder="Select shelter..." data={shelterSelectData} value={transferTo} onChange={setTransferTo} searchable required />
          <Textarea label="Reason" placeholder="Reason for transfer..." value={transferReason} onChange={e => setTransferReason(e.currentTarget.value)} minRows={2} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { setTransferOpen(false); setTransferTo(null); setTransferReason(''); }}>Cancel</Button>
            <Button color="blue" onClick={handleTransfer} loading={transferLoading} disabled={!transferTo}>Initiate Transfer</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add Medical Entry Modal */}
      <Modal opened={medicalOpen} onClose={() => setMedicalOpen(false)} title="Add Medical Entry" size="md">
        <Stack gap="md">
          <Select label="Entry Type" data={[
            { value: 'vitals', label: 'Vitals' },
            { value: 'medication', label: 'Medication' },
            { value: 'incident', label: 'Incident' },
            { value: 'note', label: 'Note' },
            { value: 'referral', label: 'Referral' },
          ]} value={medEntry.entryType} onChange={v => setMedEntry({ ...medEntry, entryType: v || 'note' })} />
          <Textarea label="Description" placeholder="Describe the entry..." value={medEntry.description} onChange={e => setMedEntry({ ...medEntry, description: e.currentTarget.value })} minRows={2} />
          {(medEntry.entryType === 'medication') && (
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="Medication Name" value={medEntry.medicationName} onChange={e => setMedEntry({ ...medEntry, medicationName: e.currentTarget.value })} />
              <TextInput label="Dosage" value={medEntry.dosage} onChange={e => setMedEntry({ ...medEntry, dosage: e.currentTarget.value })} />
            </SimpleGrid>
          )}
          <Textarea label="Notes" value={medEntry.notes} onChange={e => setMedEntry({ ...medEntry, notes: e.currentTarget.value })} minRows={1} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setMedicalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMedical} loading={medLoading} disabled={!medEntry.description.trim()}>Save Entry</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add Referral Modal */}
      <Modal opened={referralOpen} onClose={() => setReferralOpen(false)} title="Add Referral" size="md">
        <Stack gap="md">
          <Select label="Referral Type" data={REFERRAL_TYPES} value={refEntry.referralType} onChange={v => setRefEntry({ ...refEntry, referralType: v || 'fema' })} />
          <TextInput label="Agency" placeholder="Agency name..." value={refEntry.agency} onChange={e => setRefEntry({ ...refEntry, agency: e.currentTarget.value })} />
          <Textarea label="Notes" value={refEntry.notes} onChange={e => setRefEntry({ ...refEntry, notes: e.currentTarget.value })} minRows={2} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setReferralOpen(false)}>Cancel</Button>
            <Button color="violet" onClick={handleAddReferral} loading={refLoading}>Create Referral</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ── Small helper for display fields ──────────────────────────
function InfoField({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div>
      <Group gap={4} mb={2}>
        {icon}
        <Text size="xs" fw={600} c="dimmed">{label}</Text>
      </Group>
      <Text size="sm">{value}</Text>
    </div>
  );
}
